import { HTMLAttributes, useCallback, useEffect, useState } from 'react'
import { useMedia } from 'react-use'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import Link from 'next/link'

import Button from '@/components/base/button'
import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Media from '@/components/base/media'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { Card } from '@/components/sections/grid'
import { MediaQuery } from '@/constants'

import { useHomeClinicMotion } from './motion'
import Spacer from './spacer'
import { cn } from '@/lib/utils'
import { WPImage, WPLink, WPMedia } from '@/types'

export type HomeClinicProps = HTMLAttributes<HTMLElement> &
  Partial<{
    paragraph: {
      title: string
      text: string
      link: WPLink
    }
    grid: {
      title: string
      items: {
        title: string
        text: string
        link: string
        image: WPImage
      }[]
    }
    media: WPMedia
  }>

const HomeClinic = ({ paragraph, grid, media, className, ...props }: HomeClinicProps) => {
  const { title, text, link } = paragraph || {}
  const [emblaRef, emblaApi] = useEmblaCarousel({})
  const [selectedIndex, setSelectedIndex] = useState(0)
  const { motionRef } = useHomeClinicMotion()
  const needsCarouselMode = useMedia(MediaQuery.SMALL, true)

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    onSelect(emblaApi)
    emblaApi.reInit({ active: needsCarouselMode })
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, needsCarouselMode, onSelect])

  return (
    <Section ref={motionRef} className={cn('z-10', className)} {...props}>
      <div className="grid">
        <div className="relative col-[1/-1] row-[1/-1]">
          {(media?.poster?.url || media?.video?.url) && (
            <div className="-z-1 pointer-events-none absolute inset-0">
              {/* <div className="absolute inset-0 z-10 bg-[black]/20" /> */}
              <Media media={media} />
            </div>
          )}
        </div>

        <div className="relative col-[1/-1] row-[1/-1]">
          <Spacer variant="2xl" className="bg-transparent dark:bg-transparent" />
          <Spacer variant="2xl" className="bg-transparent dark:bg-transparent" />

          <Container>
            <Grid>
              <div
                className={cn(
                  'col-span-2 md:col-span-3 md:col-start-4 lg:col-span-5 lg:col-start-7',
                  'grid gap-y-spacing-sm md:gap-y-spacing-lg'
                )}
                data-motion-row
              >
                {title && (
                  <div className="flex">
                    <Typography
                      variant="h4"
                      className="text-text-secondary-invert-light"
                      data-motion-title
                    >
                      {title}
                    </Typography>
                  </div>
                )}
                {text && (
                  <div className="flex">
                    <Typography
                      variant="body"
                      className={cn(
                        'text-text-primary-light',
                        '[&_strong]:font-roboto [&_strong]:text-large [&_strong]:font-medium'
                      )}
                      data-motion-paragraph
                    >
                      {text}
                    </Typography>
                  </div>
                )}
                {link?.url ? (
                  <div className="flex">
                    <Button asChild variant="secondary" data-motion-button>
                      <Link href={link.url} target={link.target} scroll={false}>
                        <Typography variant="body" mask>
                          {link.title}
                        </Typography>
                      </Link>
                    </Button>
                  </div>
                ) : null}
              </div>
            </Grid>
          </Container>

          {grid?.items?.length ? (
            <>
              <Spacer variant="2xl" className="bg-transparent dark:bg-transparent" />

              <div className="grid gap-y-spacing-sm" data-motion-row>
                <Container>
                  <Grid>
                    {grid.title ? (
                      <div className="col-span-4 flex md:col-span-6 lg:col-span-5 lg:col-start-7">
                        <Typography
                          variant="large"
                          className="text-text-primary-light [&_b]:font-medium"
                        >{`<b>${grid.title}</b>`}</Typography>
                      </div>
                    ) : null}
                  </Grid>
                </Container>

                <Container ref={emblaRef} className="overflow-hidden">
                  <div
                    className={cn(
                      '-ml-margin flex',
                      'md:m-0 md:grid md:grid-cols-6 md:gap-gutter lg:grid-cols-12'
                    )}
                  >
                    {grid.items.map((card, index) => (
                      <div
                        key={`card-${index}`}
                        className={cn(
                          'min-w-0 shrink-0 grow-0 basis-full pl-margin',
                          'col-span-2 md:col-span-3 md:p-0 lg:col-span-4'
                        )}
                      >
                        <Card
                          className={cn('h-full w-full text-text-primary-light')}
                          data-motion-card
                          {...card}
                        />
                      </div>
                    ))}
                  </div>

                  {needsCarouselMode ? (
                    <div className="mt-spacing-sm flex items-center justify-center">
                      {grid.items?.map((_, index) => (
                        <div
                          key={`gallery-dot-${index}`}
                          style={{ left: `${-1.6 * selectedIndex + 1.6}rem` }}
                          className={cn(
                            'relative',
                            'transition-all duration-300 ease-out-cubic',
                            'mx-[0.4rem] h-[0.8rem] w-[0.8rem] scale-0 rounded-full bg-surface-accent-light',
                            {
                              'scale-[1]': index === selectedIndex,
                              'scale-[0.6]':
                                index === selectedIndex - 1 || index === selectedIndex + 1,
                              'scale-[0.3]':
                                index === selectedIndex - 2 || index === selectedIndex + 2
                            }
                          )}
                        />
                      ))}
                    </div>
                  ) : null}
                </Container>
              </div>
            </>
          ) : null}

          <Spacer variant="2xl" className="bg-transparent dark:bg-transparent" />
          <Spacer variant="2xl" className="bg-transparent dark:bg-transparent" />
        </div>
      </div>
    </Section>
  )
}

export default HomeClinic
